import { CodeBook, PagedResult } from "@king-ict/eupisi-ui/models/Common";
import { axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi, skipToken } from "@reduxjs/toolkit/query/react";

import Announcement from "../models/Announcement/Announcement";
import ImportantDate from "../models/ImportantDate/ImportantDate";
import { InstitutionCodeBook } from "../models/Institution/InstitutionCodeBook";
import InstitutionDetails from "../models/Institution/InstitutionDetails";
import InstitutionProgram from "../models/Institution/InstitutionProgram";
import { ReligiousAndMinorityInstitutionsDto } from "../models/Institution/ReligiousAndMinorityInstitutionsDto";
import { SchoolYear } from "../models/SchoolYear";

const alternativeDocumentsUrlPrefix: string = "alternative-documents";
const schoolYearsUrlPrefix: string = "school-years";
const importantDatesUrlPrefix: string = "importantDates";
const institutionsUrlPrefix: string = "institutions";

export interface IGetInstitutionQueryModel {
  institutionId: number;
  schoolYear: number;
  isNationalMinorityOrReligious?: boolean;
}

export interface IGetInstitutionProgramQueryModel {
  institutionId: number;
  schoolYearId: number;
  programId: number | null;
}

export interface IGetFirstAssignedInstitutionQueryModel {
  schoolCandidateId?: number | null;
  schoolYearId?: number | null;
  hasMinorityOrReligiousSchool: boolean;
}

export const dataApi = createApi({
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    /*****************************************/
    /* Data */
    getAnnouncements: build.query<Announcement[], void>({
      query: () => "announcement",
    }),
    getCurrentSchoolYear: build.query<SchoolYear, void>({
      query: () => `${schoolYearsUrlPrefix}/current`,
    }),
    getInstitutionDetails: build.query<
      InstitutionDetails,
      IGetInstitutionQueryModel
    >({
      query: (arg) =>
        `${institutionsUrlPrefix}/${arg?.institutionId}/${arg?.schoolYear}/${
          arg?.isNationalMinorityOrReligious ?? false
        }`,
    }),
    getImportantDateAllPublic: build.query<PagedResult<ImportantDate>, void>({
      query: () => `${importantDatesUrlPrefix}/public`,
    }),
    getReligiousAndMinorityInstitutions: build.query<
      ReligiousAndMinorityInstitutionsDto,
      number | undefined
    >({
      query: (countyId) => {
        if (!countyId) throw skipToken;

        // TODO: Remove the 0 after the countyId after the backend is updated
        return `${institutionsUrlPrefix}/religious-minority-institutions/${countyId}/0`;
      },
    }),
    getFirstAssignedInstitution: build.query<
      InstitutionDetails,
      IGetFirstAssignedInstitutionQueryModel
    >({
      query: ({
        schoolCandidateId,
        schoolYearId,
        hasMinorityOrReligiousSchool,
      }) => {
        if (
          !schoolCandidateId ||
          !schoolYearId ||
          !hasMinorityOrReligiousSchool
        )
          throw skipToken;

        return `${institutionsUrlPrefix}/first-assigned-institution/${schoolCandidateId}/${schoolYearId}`;
      },
    }),
    getInstitutionProgram: build.query<
      InstitutionProgram,
      IGetInstitutionProgramQueryModel
    >({
      query: (arg) => {
        const { institutionId, schoolYearId, programId } = arg;

        if (!institutionId || !schoolYearId || !programId) throw skipToken;

        return `institution-programs/institution-program/${institutionId}/${schoolYearId}/${programId}`;
      },
    }),

    /*****************************************/
    /* CodeBook */
    getInstitutionCodeBookList: build.query<InstitutionCodeBook[], void>({
      query: () => `${institutionsUrlPrefix}/institutions-codebook-list`,
    }),
    getInstitutionChangeReasons: build.query<CodeBook[], void>({
      query: () => `${institutionsUrlPrefix}/institution-change-reasons`,
    }),
    getAlternativeDocumentReasons: build.query<CodeBook[], void>({
      query: () => `${alternativeDocumentsUrlPrefix}/alternative-code-reasons`,
    }),
    getAlternativeDocumentTypes: build.query<CodeBook[], void>({
      query: () => `${alternativeDocumentsUrlPrefix}/alternative-code-types`,
    }),
    getNationalities: build.query<CodeBook[], void>({
      query: () => "nationalities",
    }),
    getCitizenships: build.query<CodeBook[], void>({
      query: () => "citizenships",
    }),
    getNationalMinorities: build.query<CodeBook[], void>({
      query: () => "national-minorities",
    }),
    getCountries: build.query<CodeBook[], void>({
      query: () => "geography/countries",
    }),
    getCounties: build.query<CodeBook[], void>({
      query: () => "geography/counties",
    }),
    getGenders: build.query<CodeBook[], void>({
      query: () => "genders",
    }),
    getParentshipTypes: build.query<CodeBook[], void>({
      query: () => "parentship-types",
    }),
    getParentNumbers: build.query<CodeBook[], void>({
      query: () => "parent-numbers",
    }),
    getContactMethods: build.query<CodeBook[], void>({
      query: () => "contact-methods",
    }),
    getProfessionalQualifications: build.query<CodeBook[], void>({
      query: () => "professional-qualifications",
    }),
  }),
});

export const {
  /* Data */
  useGetAnnouncementsQuery,
  useGetCurrentSchoolYearQuery,
  useGetImportantDateAllPublicQuery,
  /**
   * Institutions which are active in the current
   * school year and have at least 1 active program.
   */
  useGetInstitutionCodeBookListQuery,
  useGetInstitutionChangeReasonsQuery,
  useGetAlternativeDocumentReasonsQuery,
  useGetAlternativeDocumentTypesQuery,
  useGetNationalitiesQuery,
  useGetCitizenshipsQuery,
  useGetNationalMinoritiesQuery,
  useGetCountriesQuery,
  useGetCountiesQuery,
  useGetGendersQuery,
  useGetParentshipTypesQuery,
  useGetParentNumbersQuery,
  useGetContactMethodsQuery,
  useGetProfessionalQualificationsQuery,
  useGetInstitutionDetailsQuery,
  useGetReligiousAndMinorityInstitutionsQuery,
  useGetFirstAssignedInstitutionQuery,
  useGetInstitutionProgramQuery,
} = dataApi;
